import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { CreateTeamPanel } from '../components/CreateTeamModal';
import { useStorage } from '../storage/StorageContext';
import { useDialog } from '../components/DialogProvider';
import { Team } from '../types';
import { importRoster } from '../utils/rosterImports/orchestrator';
import { rosterImportRegistry } from '../utils/rosterImports/registry';
import '../App.css';

export default function HomePage() {
  const navigate = useNavigate();
  const storage = useStorage();
  const { alert, confirm, toast } = useDialog();
  const [teams, setTeams] = useState<Team[]>([]);
  const [showLoadSection, setShowLoadSection] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportSection, setShowImportSection] = useState(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState('tourplay-pdf');
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    storage.listTeams().then(setTeams);
  }, [storage]);

  const activeStrategies = rosterImportRegistry.getStrategies();

  const handleImportFile = async (file: File) => {
    setIsImporting(true);
    try {
      const newTeam = await importRoster(file, selectedStrategyId);

      // Save to IndexedDB and Navigate
      const id = await storage.saveTeam(newTeam);
      toast(`Successfully imported "${newTeam.teamName}"!`, 'success');
      navigate(`/roster/${id}`);
    } catch (err: any) {
      console.error('Import failed:', err);
      await alert(`Roster import failed: ${err.message || err}`, 'Import Error');
    } finally {
      setIsImporting(false);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (isImporting) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleImportFile(files[0]);
    }
  };

  const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleImportFile(files[0]);
    }
  };

  return (
    <>
      <Header status="Create, Load or Import a Team" />
      <div className="home-controls">
        {/* Navigate to the RosterPage */}
        <button className="home-btn" onClick={async () => {
          const draftsCount = teams.filter(t => !t.isRegistered).length;
          if (draftsCount >= 10) {
            await alert('You have reached the maximum limit of 10 draft teams. Please delete or register a draft before creating a new one.', 'Draft Limit Reached');
            return;
          }
          setShowCreateModal(!showCreateModal);
          setShowLoadSection(false);
          setShowImportSection(false);
        }}>
          New Team
        </button>
        {/* Toggle Load Team selection list */}
        <button className="home-btn" onClick={() => {
          setShowLoadSection(!showLoadSection);
          setShowCreateModal(false);
          setShowImportSection(false);
        }}>
          {showLoadSection ? 'Hide Saved Teams' : 'Load Team'}
        </button>

        {/* Toggle Import Roster panel */}
        <button className="home-btn" onClick={() => {
          setShowImportSection(!showImportSection);
          setShowCreateModal(false);
          setShowLoadSection(false);
        }} style={{ backgroundColor: '#2563eb' }}>
          {showImportSection ? 'Hide Import' : 'Import Roster'}
        </button>
        
        {/* OTT Simulator Entry */}
        <button className="home-btn" onClick={() => navigate('/ott')} style={{ backgroundColor: '#8b0000' }}>
          OTT Simulator
        </button>
      </div>

      {showCreateModal && (
        <CreateTeamPanel 
          onClose={() => setShowCreateModal(false)}
          onSubmit={(teamName, region, rulesetMode, goldBudget, tier) => {
            setShowCreateModal(false);
            navigate('/roster', { state: { newTeam: { teamName, region, rulesetMode, goldBudget, tier } } });
          }}
        />
      )}

      {showImportSection && (
        <div className="panel" style={{ maxWidth: '450px', margin: '0 auto 32px auto', padding: '24px', border: '1px solid #334155', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)', backgroundColor: '#1e293b', color: '#f8fafc' }}>
          <h3 style={{ marginTop: 0, marginBottom: '16px', textAlign: 'center', fontSize: '18px', fontWeight: 'bold' }}>Import Team Roster</h3>
          
          <div style={{ marginBottom: '18px' }}>
            <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Import Platform</label>
            <select 
              value={selectedStrategyId}
              onChange={(e) => setSelectedStrategyId(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #475569', backgroundColor: '#0f172a', color: '#f8fafc', fontSize: '14px', outline: 'none' }}
              disabled={isImporting}
            >
              {activeStrategies.map(strat => (
                <option key={strat.id} value={strat.id}>{strat.name}</option>
              ))}
            </select>
          </div>

          <div 
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            style={{ 
              border: isDragging ? '2px dashed #2563eb' : '2px dashed #475569', 
              borderRadius: '8px', 
              padding: '40px 20px', 
              textAlign: 'center', 
              backgroundColor: isDragging ? 'rgba(37, 99, 235, 0.05)' : '#0f172a', 
              cursor: isImporting ? 'default' : 'pointer',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
            onClick={() => {
              if (!isImporting) {
                document.getElementById('roster-file-input')?.click();
              }
            }}
          >
            <input 
              id="roster-file-input"
              type="file" 
              accept={rosterImportRegistry.getStrategy(selectedStrategyId)?.acceptFileType}
              onChange={onFileSelect}
              style={{ display: 'none' }}
              disabled={isImporting}
            />

            {isImporting ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <div className="spinner" style={{ width: '32px', height: '32px', border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                <span style={{ fontSize: '14px', color: '#94a3b8' }}>Parsing and importing roster...</span>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>📁</div>
                <strong style={{ display: 'block', fontSize: '14px', color: '#cbd5e1', marginBottom: '4px' }}>
                  Drag & Drop printed roster sheet
                </strong>
                <span style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                  or click here to browse files
                </span>
                <span style={{ display: 'inline-block', fontSize: '11px', color: '#2563eb', backgroundColor: 'rgba(37,99,235,0.1)', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                  Supports {rosterImportRegistry.getStrategy(selectedStrategyId)?.acceptFileType.toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <p style={{ margin: '14px 0 0 0', fontSize: '11px', color: '#64748b', textAlign: 'center', lineHeight: '1.4' }}>
            {rosterImportRegistry.getStrategy(selectedStrategyId)?.description}
          </p>
        </div>
      )}

      {showLoadSection && (
        <div className="panel" style={{ maxWidth: '400px', margin: '0 auto 32px auto', padding: '20px', border: '1px solid #334155', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)', backgroundColor: '#1e293b', color: '#f8fafc' }}>
          <h3 style={{ marginTop: 0, marginBottom: '16px', textAlign: 'center', fontSize: '18px', fontWeight: 'bold' }}>Select a Team to Load</h3>
          {teams.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '14px', margin: 0 }}>No teams saved yet. Create a new team first!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Drafts Section */}
              {teams.filter(t => !t.isRegistered).length > 0 && (
                <div>
                  <h4 style={{ margin: '0 0 10px 0', borderBottom: '1px solid #334155', paddingBottom: '4px', color: '#cbd5e1' }}>Drafts</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {teams.filter(t => !t.isRegistered).map((t) => (
                      <div key={t.id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          className="home-btn"
                          style={{ flex: 1, textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', fontSize: '14px' }}
                          onClick={() => navigate(`/roster/${t.id}`)}
                        >
                          <span style={{ fontWeight: 'bold' }}>{t.teamName}</span>
                          <span style={{ fontSize: '12px', opacity: 0.8 }}>({t.region})</span>
                        </button>
                        <button
                          style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', padding: '10px 12px', cursor: 'pointer', fontSize: '14px', transition: 'background-color 0.2s' }}
                          onClick={async () => {
                            if (await confirm(`Are you sure you want to delete draft ${t.teamName}?`, 'Delete Draft')) {
                              await storage.deleteTeam(t.id);
                              setTeams(prev => prev.filter(x => x.id !== t.id));
                              toast(`Deleted draft team "${t.teamName}"`, 'success');
                            }
                          }}
                          title="Delete Draft"
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Registered Section */}
              {teams.filter(t => t.isRegistered).length > 0 && (
                <div>
                  <h4 style={{ margin: '0 0 10px 0', borderBottom: '1px solid #334155', paddingBottom: '4px', color: '#22c55e' }}>Registered Teams</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {teams.filter(t => t.isRegistered).map((t) => (
                      <div key={t.id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          className="home-btn"
                          style={{ flex: 1, textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', fontSize: '14px', borderLeft: '4px solid #16a34a' }}
                          onClick={() => navigate(`/roster/${t.id}`)}
                        >
                          <span style={{ fontWeight: 'bold' }}>{t.teamName}</span>
                          <span style={{ fontSize: '12px', opacity: 0.8 }}>({t.region})</span>
                        </button>
                        <button
                          style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', padding: '10px 12px', cursor: 'pointer', fontSize: '14px', transition: 'background-color 0.2s' }}
                          onClick={async () => {
                            if (await confirm(`Are you sure you want to delete registered team ${t.teamName}?`, 'Delete Registered Team')) {
                              await storage.deleteTeam(t.id);
                              setTeams(prev => prev.filter(x => x.id !== t.id));
                              toast(`Deleted registered team "${t.teamName}"`, 'success');
                            }
                          }}
                          title="Delete Team"
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
